import React, { useEffect, useState } from "react";
import { useT } from "@/i18n/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageTitle, Panel, Btn, Field, Input, Select, Textarea, Modal, DataTable, Plus, toast } from "@/pages/app/kit";
import { DemoBadge, StatusBadge } from "@/components/shared";

const EMPTY = { process_type: "Freezing", product_accepted: "", available_date: "", available_quantity: 0, min_batch: 0, max_batch: 0, estimated_cost: 0, yield_pct: 0, turnaround_days: 0, finished_format: "", region: "lubelskie", notes: "" };

export default function ProcessingPage() {
  const { t } = useT();
  const { user } = useAuth();
  const [caps, setCaps] = useState([]);
  const [allocs, setAllocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const canCreate = ["processor", "coordinator", "admin", "super_admin"].includes(user.role);

  const load = () => {
    api.get("/processing/capacity").then((r) => setCaps(r.data)).catch(() => {});
    api.get("/processing/allocations").then((r) => setAllocs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = { ...form };
      ["available_quantity","min_batch","max_batch","estimated_cost","yield_pct","turnaround_days"].forEach((k)=>p[k]=parseFloat(p[k])||0);
      await api.post("/processing/capacity", p);
      toast.success(t("cta.create")); setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const capCols = [
    { key: "process_type", label: "Process", render: (r) => <div><span className="font-semibold">{r.process_type}</span>{r.is_demo && <DemoBadge className="ml-2" />}<div className="text-xs text-mutedtext">{r.owner_name}</div></div> },
    { key: "product_accepted", label: "Accepts" },
    { key: "available_quantity", label: "Available", render: (r) => `${r.available_quantity?.toLocaleString()} kg` },
    { key: "yield_pct", label: "Yield", render: (r) => `${r.yield_pct}%` },
    { key: "estimated_cost", label: "Cost/kg", render: (r) => `${r.estimated_cost} PLN` },
    { key: "finished_format", label: "Output" },
  ];
  const allocCols = [
    { key: "crop_product", label: "Crop" },
    { key: "process_type", label: "Process" },
    { key: "processor_name", label: "Processor" },
    { key: "quantity_sent", label: "Sent", render: (r) => `${r.quantity_sent?.toLocaleString()} kg` },
    { key: "output_quantity", label: "Output", render: (r) => `${(r.output_quantity||0).toLocaleString()} kg` },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <div>
      <PageTitle title={t("dashboard.capacity")} action={canCreate && <Btn icon={Plus} onClick={() => setOpen(true)} data-testid="add-capacity-btn">{t("cta.create")}</Btn>} />
      <Panel title="Capacity" className="mb-6"><DataTable columns={capCols} rows={caps} empty={t("empty")} testid="capacity-table" /></Panel>
      <Panel title="Allocations"><DataTable columns={allocCols} rows={allocs} empty={t("empty")} testid="allocations-table" /></Panel>

      <Modal open={open} onClose={() => setOpen(false)} title={t("dashboard.capacity")} wide>
        <form onSubmit={submit} className="space-y-4" data-testid="capacity-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Process type" required><Select data-testid="cap-type" value={form.process_type} onChange={set("process_type")}>
              {["Cold storage","Freezing","Juicing","Drying","Preserving","Fermentation","Vegetable prep","Animal feed","Biogas","Composting"].map((g)=><option key={g}>{g}</option>)}
            </Select></Field>
            <Field label="Product accepted"><Input value={form.product_accepted} onChange={set("product_accepted")} placeholder="Cherries" /></Field>
            <Field label="Available quantity (kg)"><Input data-testid="cap-qty" type="number" value={form.available_quantity} onChange={set("available_quantity")} /></Field>
            <Field label="Available date"><Input type="date" value={form.available_date?.slice(0,10)} onChange={set("available_date")} /></Field>
            <Field label="Yield %"><Input type="number" value={form.yield_pct} onChange={set("yield_pct")} /></Field>
            <Field label="Cost/kg (PLN)"><Input type="number" step="0.01" value={form.estimated_cost} onChange={set("estimated_cost")} /></Field>
            <Field label="Turnaround (days)"><Input type="number" value={form.turnaround_days} onChange={set("turnaround_days")} /></Field>
            <Field label="Finished format"><Input value={form.finished_format} onChange={set("finished_format")} /></Field>
          </div>
          <Field label={t("crop.notes")}><Textarea rows={2} value={form.notes} onChange={set("notes")} /></Field>
          <div className="flex gap-3"><Btn type="submit" disabled={busy} data-testid="cap-submit">{busy?"…":t("cta.create")}</Btn><Btn type="button" variant="ghost" onClick={()=>setOpen(false)}>{t("cta.cancel")}</Btn></div>
        </form>
      </Modal>
    </div>
  );
}
